import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Modal, Input, DataTable, Spinner } from '@/components/ui';
import {
  UserPlus,
  Mail,
  Edit,
  Trash2,
  Building2,
  AlertCircle,
  ShieldCheck,
  WrenchIcon,
  Copy,
  Send,
  Clock,
  KeyRound,
} from 'lucide-react';
import { cn, formatRelativeTime } from '@/lib/utils';
import { toDate } from '@/lib/date-utils';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { collection, doc, getDoc, getDocs, setDoc, Timestamp, serverTimestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { TimeZonePicker } from '@/components/ui/TimeZonePicker';
import { AUTO_TIME_ZONE, formatZoneLabel } from '@simcheck/shared-types';
import type { OrganizationMember, UserRole, Invitation } from '@/types/auth';
import { sendMemberPasswordResetLink, getMembersActivity } from '@/services/orgMemberService';

export const PLAN_CONFIG: Record<string, { label: string; className: string }> = {
  trial:      { label: 'Trial',      className: 'bg-accent-blue/10 text-accent-blue' },
  prototyper: { label: 'Prototyper', className: 'bg-status-pending-bg text-status-pending' },
  starter:    { label: 'Starter',    className: 'bg-status-pending-bg text-status-pending' },
  business:   { label: 'Business',   className: 'bg-status-success-bg text-status-success' },
  enterprise: { label: 'Enterprise', className: 'bg-accent-purple/10 text-accent-purple' },
};

/** Label + badge class for any plan id, including `custom_<slug>` enterprise clones. */
export function planConfigFor(plan?: string): { label: string; className: string } {
  if (!plan) return { label: 'No Plan', className: 'bg-muted text-muted-foreground' };
  if (PLAN_CONFIG[plan]) return PLAN_CONFIG[plan];
  if (plan.startsWith('custom_')) return { label: 'Custom', className: 'bg-accent-purple/10 text-accent-purple' };
  return { label: plan, className: 'bg-muted text-muted-foreground' };
}

export function OrgSettingsPanel() {
  const {
    activeOrganization,
    activeOrganizationId,
    currentUser,
    updateOrganization,
    inviteUser,
    removeUser,
    updateUserRole,
    getPendingInvitationsForOrg,
    revokeInvitation,
    hasPermission,
    isSuperAdmin,
    refreshOrganizations,
  } = useAuth();
  const { success, error: showError } = useToast();

  const canManageMembers = hasPermission('users:invite');

  // Org default time zone. Absent → the `'auto'` sentinel, meaning "not set";
  // `resolveTimeZone` then falls through to each member's browser zone.
  const orgTimezone = activeOrganization?.settings?.reports?.defaultTimezone || AUTO_TIME_ZONE;
  const orgZoneLabel = orgTimezone === AUTO_TIME_ZONE ? '' : formatZoneLabel(orgTimezone, new Date());
  const [orgTzSaving, setOrgTzSaving] = useState(false);

  const handleOrgTimezoneChange = async (tz: string) => {
    setOrgTzSaving(true);
    try {
      await updateOrganization({ 'settings.reports.defaultTimezone': tz } as any);
      success(
        tz === AUTO_TIME_ZONE
          ? 'Cleared the organization time zone'
          : `Organization time zone set to ${tz}`,
      );
    } catch (err) {
      console.error('Failed to save organization timezone:', err);
      showError('Failed to update organization time zone');
    } finally {
      setOrgTzSaving(false);
    }
  };

  // Team members state (only loaded for admin/owner)
  const [teamMembers, setTeamMembers] = useState<OrganizationMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(canManageMembers);

  // Membership repair state — shown when member doc is missing
  const [memberDocMissing, setMemberDocMissing] = useState(false);
  const [repairing, setRepairing] = useState(false);

  // Invite modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Exclude<UserRole, 'owner'>>('member');
  const [inviting, setInviting] = useState(false);

  // Edit org modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit member role modal
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<OrganizationMember | null>(null);
  const [editMemberRole, setEditMemberRole] = useState<UserRole>('member');
  const [updatingRole, setUpdatingRole] = useState(false);

  // Delete member modal
  const [showDeleteMemberModal, setShowDeleteMemberModal] = useState(false);
  const [deletingMember, setDeletingMember] = useState(false);
  const [resettingMemberId, setResettingMemberId] = useState<string | null>(null);
  // Real last-active per member (Auth-sourced), keyed by userId. `null` = never
  // signed in; a missing key = not loaded yet. The member-doc `lastSeenAt` was
  // only a join stamp, so we don't render it.
  const [memberActivity, setMemberActivity] = useState<Record<string, number | null>>({});

  // Pending invitations
  const [pendingInvites, setPendingInvites] = useState<Invitation[]>([]);
  const [invitesLoading, setInvitesLoading] = useState(canManageMembers);
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(null);
  const [revokeInvite, setRevokeInvite] = useState<Invitation | null>(null);
  const [revokingInvite, setRevokingInvite] = useState(false);

  // Load team members — only for users who can manage them (admin/owner).
  // member/viewer cannot list the members collection (Firestore rules block it).
  useEffect(() => {
    if (!activeOrganizationId || !canManageMembers) {
      setMembersLoading(false);
      return;
    }
    const loadMembers = async () => {
      try {
        setMembersLoading(true);
        setMemberDocMissing(false);

        // First check if the current user's own member doc exists.
        // If it's missing, the collection list query will fail with permission-denied.
        // Super admins are exempt: Firestore rules let them list any org's members even
        // when they aren't a member (e.g. viewing a tenant they don't belong to), so
        // running this pre-check for them would falsely blank the list and show the
        // "membership missing" banner for an org they're merely impersonating.
        if (currentUser && !isSuperAdmin) {
          const myMemberRef = doc(db, 'organizations', activeOrganizationId, 'members', currentUser.uid);
          const myMemberSnap = await getDoc(myMemberRef);
          if (!myMemberSnap.exists()) {
            setMemberDocMissing(true);
            setMembersLoading(false);
            return;
          }
        }

        const membersRef = collection(db, 'organizations', activeOrganizationId, 'members');
        const snap = await getDocs(membersRef);
        const members: OrganizationMember[] = snap.docs.map((d) => {
          const data = d.data();
          return {
            userId: d.id,
            email: data.email || '',
            displayName: data.displayName || null,
            photoURL: data.photoURL || null,
            role: data.role || 'member',
            joinedAt: data.joinedAt || Timestamp.now(),
            invitedBy: data.invitedBy || null,
            lastSeenAt: data.lastSeenAt || data.joinedAt || Timestamp.now(),
          };
        });
        members.sort((a, b) => {
          if (a.role === 'owner') return -1;
          if (b.role === 'owner') return 1;
          return (a.displayName || a.email).localeCompare(b.displayName || b.email);
        });
        setTeamMembers(members);

        // Best-effort: overlay real last-active from Auth. Never block the list on it.
        getMembersActivity(activeOrganizationId)
          .then((r) => setMemberActivity(
            Object.fromEntries(Object.entries(r.activity).map(([uid, v]) => [uid, v.lastActiveAt])),
          ))
          .catch((e) => console.warn('Failed to load member activity:', e));
      } catch (err) {
        console.error('Failed to load members:', err);
        showError('Failed to load team members');
      } finally {
        setMembersLoading(false);
      }
    };
    loadMembers();
  }, [activeOrganizationId, canManageMembers, currentUser, isSuperAdmin]);

  const loadPendingInvites = async () => {
    if (!activeOrganizationId || !canManageMembers) {
      setInvitesLoading(false);
      return;
    }
    try {
      setInvitesLoading(true);
      const invites = await getPendingInvitationsForOrg(activeOrganizationId);
      setPendingInvites(invites);
    } catch (err) {
      console.error('Failed to load pending invitations:', err);
    } finally {
      setInvitesLoading(false);
    }
  };

  useEffect(() => {
    loadPendingInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrganizationId, canManageMembers]);

  const handleRepairMembership = async () => {
    if (!currentUser || !activeOrganizationId || !activeOrganization) return;
    try {
      setRepairing(true);
      const memberRef = doc(db, 'organizations', activeOrganizationId, 'members', currentUser.uid);
      await setDoc(memberRef, {
        role: 'owner',
        joinedAt: serverTimestamp(),
        invitedBy: null,
        email: currentUser.email,
        displayName: currentUser.displayName || null,
        photoURL: currentUser.photoURL || null,
        lastSeenAt: serverTimestamp(),
      });
      await refreshOrganizations();
      setMemberDocMissing(false);
      success('Membership restored — you can now manage your organization');
      // Reload members list
      const membersRef = collection(db, 'organizations', activeOrganizationId, 'members');
      const snap = await getDocs(membersRef);
      const members: OrganizationMember[] = snap.docs.map((d) => {
        const data = d.data();
        return {
          userId: d.id,
          email: data.email || '',
          displayName: data.displayName || null,
          photoURL: data.photoURL || null,
          role: data.role || 'member',
          joinedAt: data.joinedAt || Timestamp.now(),
          invitedBy: data.invitedBy || null,
          lastSeenAt: data.lastSeenAt || data.joinedAt || Timestamp.now(),
        };
      });
      members.sort((a, b) => {
        if (a.role === 'owner') return -1;
        if (b.role === 'owner') return 1;
        return (a.displayName || a.email).localeCompare(b.displayName || b.email);
      });
      setTeamMembers(members);
    } catch (err: any) {
      showError(err.message || 'Failed to repair membership');
    } finally {
      setRepairing(false);
    }
  };

  const handleEditClick = () => {
    if (!activeOrganization) return;
    setEditName(activeOrganization.name);
    setShowEditModal(true);
  };

  const handleSaveOrganization = async () => {
    if (!editName.trim() || !activeOrganization) return;
    try {
      setSaving(true);
      await updateOrganization({ name: editName.trim() });
      success('Organization updated successfully');
      setShowEditModal(false);
    } catch (err: any) {
      showError(err.message || 'Failed to update organization');
    } finally {
      setSaving(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      setInviting(true);
      await inviteUser(inviteEmail.trim(), inviteRole);
      success(`Invitation sent to ${inviteEmail}`);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('member');
      loadPendingInvites();
    } catch (err: any) {
      showError(err.message || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const buildAcceptUrl = (inv: Invitation) => {
    const base = window.location.origin;
    return `${base}/signup?token=${encodeURIComponent(inv.token)}&email=${encodeURIComponent(inv.email)}`;
  };

  const handleCopyAcceptLink = async (inv: Invitation) => {
    try {
      await navigator.clipboard.writeText(buildAcceptUrl(inv));
      success('Accept link copied to clipboard');
    } catch {
      showError('Failed to copy link');
    }
  };

  const handleResendInvite = async (inv: Invitation) => {
    try {
      setResendingInviteId(inv.id);
      await inviteUser(inv.email, inv.role as Exclude<UserRole, 'owner'>);
      success(`Invitation re-sent to ${inv.email}`);
      loadPendingInvites();
    } catch (err: any) {
      showError(err.message || 'Failed to resend invitation');
    } finally {
      setResendingInviteId(null);
    }
  };

  const handleConfirmRevokeInvite = async () => {
    if (!revokeInvite) return;
    try {
      setRevokingInvite(true);
      await revokeInvitation(revokeInvite.id);
      success('Invitation revoked');
      setPendingInvites((prev) => prev.filter((i) => i.id !== revokeInvite.id));
      setRevokeInvite(null);
    } catch (err: any) {
      showError(err.message || 'Failed to revoke invitation');
    } finally {
      setRevokingInvite(false);
    }
  };

  const handleEditMember = (member: OrganizationMember) => {
    setSelectedMember(member);
    setEditMemberRole(member.role);
    setShowEditMemberModal(true);
  };

  const handleUpdateMemberRole = async () => {
    if (!selectedMember) return;
    try {
      setUpdatingRole(true);
      await updateUserRole(selectedMember.userId, editMemberRole);
      success('Member role updated');
      setShowEditMemberModal(false);
      setTeamMembers((prev) =>
        prev.map((m) =>
          m.userId === selectedMember.userId ? { ...m, role: editMemberRole } : m
        )
      );
    } catch (err: any) {
      showError(err.message || 'Failed to update role');
    } finally {
      setUpdatingRole(false);
    }
  };

  const handleDeleteMember = (member: OrganizationMember) => {
    setSelectedMember(member);
    setShowDeleteMemberModal(true);
  };

  // Email a reset link to the member's own address — we never see it, so this is
  // safe for an org admin to trigger for anyone (including a locked-out owner).
  const handleSendResetLink = async (member: OrganizationMember) => {
    if (!activeOrganizationId) return;
    setResettingMemberId(member.userId);
    try {
      const r = await sendMemberPasswordResetLink(activeOrganizationId, member.userId);
      success(`Password-reset link sent to ${r.sentTo}`);
    } catch (err: any) {
      showError(err?.message || 'Could not send a reset link');
    } finally {
      setResettingMemberId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedMember) return;
    try {
      setDeletingMember(true);
      await removeUser(selectedMember.userId);
      success('Member removed from organization');
      setShowDeleteMemberModal(false);
      setTeamMembers((prev) => prev.filter((m) => m.userId !== selectedMember.userId));
    } catch (err: any) {
      showError(err.message || 'Failed to remove member');
    } finally {
      setDeletingMember(false);
    }
  };

  const getInitials = (name: string) =>
    name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);

  const roleConfig = {
    owner: { color: 'text-primary', label: 'Owner' },
    admin: { color: 'text-accent-blue', label: 'Admin' },
    member: { color: 'text-accent-green', label: 'Member' },
    viewer: { color: 'text-muted-foreground', label: 'Viewer' },
  };

  if (!activeOrganization) return null;

  const plan = activeOrganization.subscription?.plan;

  return (
    <div className="space-y-6">
      {/* Organization Info Card */}
      <Card padding="lg">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-lg bg-accent-blue flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-xl">
                {getInitials(activeOrganization.name)}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {activeOrganization.name}
              </h2>
              <p className="text-muted-foreground">{activeOrganization.slug}</p>
              <div className="flex items-center gap-2 mt-2">
                {plan && PLAN_CONFIG[plan] && (
                  <Badge className={PLAN_CONFIG[plan].className}>
                    {PLAN_CONFIG[plan].label}
                  </Badge>
                )}
                <Badge variant="secondary">{teamMembers.length} Team Members</Badge>
              </div>
            </div>
          </div>
          {canManageMembers && (
            <Button variant="outline" size="sm" onClick={handleEditClick}>
              <Edit className="w-4 h-4 mr-2" />
              Edit Organization
            </Button>
          )}
        </div>
      </Card>

      {/* Device Access (super admin only) */}
      {isSuperAdmin && (
        <Card padding="lg">
          <h3 className="text-h3 font-semibold text-foreground mb-4">Device Access</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Control which device tiers this organization can see.
          </p>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={activeOrganization.settings?.isDemoOrg || false}
                onChange={async (e) => {
                  try {
                    await updateOrganization({
                      settings: { ...activeOrganization.settings, isDemoOrg: e.target.checked }
                    } as any);
                    success('Updated demo access');
                  } catch {
                    showError('Failed to update');
                  }
                }}
                className="w-4 h-4 rounded"
              />
              <div>
                <span className="text-sm text-foreground">Demo org</span>
                <p className="text-xs text-muted-foreground">Can see demo-tier devices</p>
              </div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={activeOrganization.settings?.hasLabAccess || false}
                onChange={async (e) => {
                  try {
                    await updateOrganization({
                      settings: { ...activeOrganization.settings, hasLabAccess: e.target.checked }
                    } as any);
                    success('Updated lab access');
                  } catch {
                    showError('Failed to update');
                  }
                }}
                className="w-4 h-4 rounded"
              />
              <div>
                <span className="text-sm text-foreground">Lab access</span>
                <p className="text-xs text-muted-foreground">Can see lab-tier devices</p>
              </div>
            </label>
          </div>
        </Card>
      )}

      {/* Organization default time zone (owner/admin) */}
      {hasPermission('org:update-settings') && (
        <Card padding="lg">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-h3 font-semibold text-foreground">Default Time Zone</h3>
            <Clock className="w-5 h-5 text-primary" />
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Applies to members who haven't picked a personal time zone in their own settings.
            Covers on-screen dates, report grouping, and delivered exports. A member's personal
            choice always wins.
          </p>
          <div className="flex items-center justify-between p-4 bg-card rounded border border-border">
            <div>
              <div className="font-medium text-foreground">Organization time zone</div>
              <div className="text-sm text-muted-foreground mt-1">
                {orgTimezone === AUTO_TIME_ZONE ? (
                  <>Not set — members follow their own device</>
                ) : (
                  <>Members default to <Badge variant="secondary">{orgZoneLabel}</Badge></>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {orgTzSaving && <Spinner size="sm" />}
              <TimeZonePicker
                value={orgTimezone}
                onChange={handleOrgTimezoneChange}
                extraOptions={[{ value: AUTO_TIME_ZONE, label: 'Not set (member device)' }]}
                className="min-w-[220px]"
              />
            </div>
          </div>
        </Card>
      )}

      {/* Two-factor authentication policy (owner/admin) */}
      {hasPermission('org:update-settings') && (
        <Card padding="lg">
          <h3 className="text-h3 font-semibold text-foreground mb-1">Two-Factor Authentication</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Require members to verify a second factor (authenticator app, passkey, or email) at sign-in.
          </p>
          <div className="space-y-2">
            {([
              { id: 'off', label: 'Off', desc: 'Members cannot enable two-factor authentication.' },
              { id: 'allowed', label: 'Allowed', desc: 'Members may turn on two-factor authentication for their own account.' },
              { id: 'required', label: 'Required', desc: 'Every member must set up a second factor. A grace period applies after you switch this on.' },
            ] as const).map((opt) => {
              const current = activeOrganization.settings?.security?.mfa?.mode || 'off';
              return (
                <label key={opt.id} className="flex items-start gap-3 p-3 rounded-lg border border-border cursor-pointer hover:bg-muted">
                  <input
                    type="radio"
                    name="mfa-mode"
                    checked={current === opt.id}
                    onChange={async () => {
                      try {
                        const updates: Record<string, unknown> = { 'settings.security.mfa.mode': opt.id };
                        if (opt.id === 'required' && current !== 'required') {
                          updates['settings.security.mfa.requiredSince'] = new Date();
                          if (activeOrganization.settings?.security?.mfa?.graceDays == null) {
                            updates['settings.security.mfa.graceDays'] = 7;
                          }
                        }
                        await updateOrganization(updates as any);
                        success('Updated two-factor policy');
                      } catch {
                        showError('Failed to update two-factor policy');
                      }
                    }}
                    className="w-4 h-4 mt-0.5"
                  />
                  <div>
                    <span className="text-sm font-medium text-foreground">{opt.label}</span>
                    <p className="text-xs text-muted-foreground">{opt.desc}</p>
                  </div>
                </label>
              );
            })}
          </div>
          {activeOrganization.settings?.security?.mfa?.mode === 'required' && (
            <div className="mt-4 flex items-center gap-2">
              <label className="text-sm text-foreground">Grace period (days):</label>
              <input
                type="number"
                min={0}
                max={90}
                defaultValue={activeOrganization.settings?.security?.mfa?.graceDays ?? 7}
                onBlur={async (e) => {
                  const days = Math.max(0, Math.min(90, parseInt(e.target.value || '7', 10) || 0));
                  try {
                    await updateOrganization({ 'settings.security.mfa.graceDays': days } as any);
                    success('Updated grace period');
                  } catch {
                    showError('Failed to update grace period');
                  }
                }}
                className="w-20 px-2 py-1 rounded-md border border-border bg-background text-foreground text-sm"
              />
              <span className="text-xs text-muted-foreground">before members are hard-blocked</span>
            </div>
          )}
        </Card>
      )}

      {/* Team Members */}
      <div className="space-y-4">
        {/* Membership repair banner — shown when user's member doc is missing.
            Never shown to super admins: they're expected not to be members of orgs
            they impersonate, and "Restore Membership" would wrongly inject them as owner. */}
        {memberDocMissing && !isSuperAdmin && (
          <Card padding="lg" className="border-status-warning bg-status-warning-bg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-status-warning flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-foreground">Membership record missing</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Your owner record for this organization is missing from the database. This is why you
                  can't run tests or manage members. Click below to restore it.
                </p>
                <Button
                  className="mt-3"
                  size="sm"
                  onClick={handleRepairMembership}
                  disabled={repairing}
                >
                  {repairing ? (
                    <Spinner size="sm" className="mr-2" />
                  ) : (
                    <WrenchIcon className="w-4 h-4 mr-2" />
                  )}
                  Restore Membership
                </Button>
              </div>
            </div>
          </Card>
        )}

        {canManageMembers ? (
          <>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-h3 font-semibold text-foreground">Team Members</h3>
                <p className="text-sm text-muted-foreground">
                  Manage who has access to your organization
                </p>
              </div>
              <Button onClick={() => setShowInviteModal(true)}>
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Member
              </Button>
            </div>

            {canManageMembers && (pendingInvites.length > 0 || invitesLoading) && (
              <Card padding="lg">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <h4 className="font-semibold text-foreground">Pending Invitations</h4>
                  {!invitesLoading && (
                    <Badge variant="secondary">{pendingInvites.length}</Badge>
                  )}
                </div>
                {invitesLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Spinner size="sm" /> Loading invitations…
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {pendingInvites.map((inv) => {
                      const expiresMs = inv.expiresAt?.toMillis?.() ?? 0;
                      const expired = expiresMs > 0 && expiresMs < Date.now();
                      const createdDate = toDate(inv.createdAt);
                      return (
                        <div
                          key={inv.id}
                          className="flex items-center justify-between py-3 gap-4"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-foreground truncate">
                                {inv.email}
                              </span>
                              <span className={cn('text-sm font-medium', roleConfig[inv.role as UserRole]?.color ?? 'text-muted-foreground')}>
                                {roleConfig[inv.role as UserRole]?.label ?? inv.role}
                              </span>
                              {expired && (
                                <Badge className="bg-status-error-bg text-status-error">Expired</Badge>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              Invited by {inv.invitedByName || 'someone'}
                              {createdDate ? ` · ${formatRelativeTime(createdDate)}` : ''}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCopyAcceptLink(inv)}
                              title="Copy accept link"
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResendInvite(inv)}
                              disabled={resendingInviteId === inv.id}
                              title="Resend invitation email"
                            >
                              {resendingInviteId === inv.id ? (
                                <Spinner size="sm" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setRevokeInvite(inv)}
                              title="Revoke invitation"
                            >
                              <Trash2 className="w-4 h-4 text-status-error" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            )}

            <DataTable<OrganizationMember>
              columns={[
                {
                  key: 'displayName',
                  header: 'Name',
                  sortable: true,
                  render: (_, member) => (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-medium">
                        {member.displayName?.[0]?.toUpperCase() || member.email[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-foreground">
                          {member.displayName || 'No name'}
                        </div>
                        <div className="text-sm text-muted-foreground">{member.email}</div>
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'role',
                  header: 'Role',
                  render: (_, member) => (
                    <span className={cn('font-medium', roleConfig[member.role].color)}>
                      {roleConfig[member.role].label}
                    </span>
                  ),
                },
                {
                  key: 'lastSeenAt',
                  header: 'Last Active',
                  sortable: true,
                  render: (_, member) => {
                    const last = memberActivity[member.userId];
                    // undefined = activity not loaded yet; null = never signed in.
                    if (last === undefined) return <span className="text-muted-foreground">—</span>;
                    return (
                      <span className="text-muted-foreground">
                        {last ? formatRelativeTime(new Date(last)) : 'Never'}
                      </span>
                    );
                  },
                },
                {
                  key: 'actions',
                  header: 'Actions',
                  render: (_, member) => (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSendResetLink(member)}
                        disabled={resettingMemberId === member.userId}
                        title="Email this member a password-reset link"
                      >
                        {resettingMemberId === member.userId
                          ? <Spinner size="sm" className="mr-1" />
                          : <KeyRound className="w-4 h-4 mr-1" />}
                        Reset link
                      </Button>
                      {member.role !== 'owner' && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => handleEditMember(member)}>
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteMember(member)}>
                            <Trash2 className="w-4 h-4 mr-1 text-status-error" />
                            Remove
                          </Button>
                        </>
                      )}
                    </div>
                  ),
                },
              ]}
              data={teamMembers}
              keyExtractor={(row) => row.userId}
              searchable
              searchPlaceholder="Search team members..."
              loading={membersLoading}
              emptyMessage="No team members found"
            />
          </>
        ) : (
          /* member/viewer: show their role in the org, no member list */
          <Card padding="lg">
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck className="w-5 h-5 text-muted-foreground" />
              <h3 className="text-h3 font-semibold text-foreground">Your Access</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              You have <span className="font-medium text-foreground">read-only</span> access to this
              organization. Contact an admin or owner to change your role or invite new members.
            </p>
          </Card>
        )}

        {/* Role Permissions reference */}
        <Card padding="sm">
          <h4 className="text-sm font-semibold text-foreground mb-3">Role Permissions</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="font-medium text-primary mb-1">Owner</div>
              <div className="text-sm text-muted-foreground">
                Full access to all organization settings and billing
              </div>
            </div>
            <div>
              <div className="font-medium text-accent-blue mb-1">Admin</div>
              <div className="text-sm text-muted-foreground">
                Manage team members, flows, and modems
              </div>
            </div>
            <div>
              <div className="font-medium text-accent-green mb-1">Member</div>
              <div className="text-sm text-muted-foreground">
                Create and execute flows, manage own resources
              </div>
            </div>
            <div>
              <div className="font-medium text-muted-foreground mb-1">Viewer</div>
              <div className="text-sm text-muted-foreground">
                Read-only access to flows and executions
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Invite Member Modal */}
      {showInviteModal && (
        <Modal
          isOpen={showInviteModal}
          onClose={() => { setShowInviteModal(false); setInviteEmail(''); setInviteRole('member'); }}
          title="Invite Team Member"
        >
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="colleague@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Role</label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as Exclude<UserRole, 'owner'>)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground"
              >
                <option value="admin">Admin</option>
                <option value="member">Member</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                variant="secondary"
                onClick={() => { setShowInviteModal(false); setInviteEmail(''); setInviteRole('member'); }}
                disabled={inviting}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleInvite}
                disabled={!inviteEmail.trim() || inviting}
              >
                {inviting && <Spinner size="sm" className="mr-2" />}
                Send Invitation
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Organization Modal */}
      {showEditModal && (
        <Modal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          title="Edit Organization"
        >
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Organization Name</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Enter organization name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-4">
              <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleSaveOrganization}
                disabled={!editName.trim() || saving}
              >
                {saving && <Spinner size="sm" className="mr-2" />}
                Save Changes
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Member Role Modal */}
      {showEditMemberModal && selectedMember && (
        <Modal
          isOpen={showEditMemberModal}
          onClose={() => setShowEditMemberModal(false)}
          title="Edit Member Role"
        >
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Change role for <strong>{selectedMember.displayName || selectedMember.email}</strong>
            </p>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Role</label>
              <select
                value={editMemberRole}
                onChange={(e) => setEditMemberRole(e.target.value as UserRole)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground"
              >
                <option value="admin">Admin</option>
                <option value="member">Member</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowEditMemberModal(false)}
                disabled={updatingRole}
              >
                Cancel
              </Button>
              <Button variant="default" onClick={handleUpdateMemberRole} disabled={updatingRole}>
                {updatingRole && <Spinner size="sm" className="mr-2" />}
                Update Role
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Member Modal */}
      {showDeleteMemberModal && selectedMember && (
        <Modal
          isOpen={showDeleteMemberModal}
          onClose={() => setShowDeleteMemberModal(false)}
          title="Remove Team Member"
        >
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-status-error-bg rounded-lg">
              <AlertCircle className="w-5 h-5 text-status-error flex-shrink-0 mt-0.5" />
              <div className="text-sm text-status-error">
                This will remove <strong>{selectedMember.displayName || selectedMember.email}</strong>{' '}
                from the organization. They will lose access to all organization resources.
              </div>
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteMemberModal(false)}
                disabled={deletingMember}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleConfirmDelete} disabled={deletingMember}>
                {deletingMember && <Spinner size="sm" className="mr-2" />}
                Remove Member
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Revoke Invitation Modal */}
      {revokeInvite && (
        <Modal
          isOpen={!!revokeInvite}
          onClose={() => !revokingInvite && setRevokeInvite(null)}
          title="Revoke Invitation"
        >
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-status-error-bg rounded-lg">
              <AlertCircle className="w-5 h-5 text-status-error flex-shrink-0 mt-0.5" />
              <div className="text-sm text-status-error">
                This will revoke the invitation for <strong>{revokeInvite.email}</strong>. The accept
                link in the email they received will stop working.
              </div>
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                variant="secondary"
                onClick={() => setRevokeInvite(null)}
                disabled={revokingInvite}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleConfirmRevokeInvite} disabled={revokingInvite}>
                {revokingInvite && <Spinner size="sm" className="mr-2" />}
                Revoke Invitation
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
